import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';

import { IStrapiDocument, ResetPassword } from 'src/app/interfaces/strapi';
import { AuthService } from 'src/app/services/auth.service';

import StrapiService from 'src/app/services/strapi.service';
import { PageComponent } from 'src/app/shared/components/page/page.component';
import { Regexes } from 'src/app/shared/regexes';
import { NgIf } from '@angular/common';
import environment from '../../../../environments/environment';
import { ErrorBlockComponent } from '../../../shared/components/error-block/error-block.component';
import { SuccessBlockComponent } from '../../../shared/components/success-block/success-block.component';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    imports: [
        NgIf,
        ErrorBlockComponent,
        FormsModule,
        ReactiveFormsModule,
        SuccessBlockComponent,
    ],
})
export class ResetPasswordComponent extends PageComponent implements OnInit {
  public override pageData: ResetPassword = <ResetPassword>{};

  private cdr = inject(ChangeDetectorRef);

  public password = new UntypedFormControl(null, [
    Validators.required.bind(this),
    Validators.pattern(Regexes.passwordRegex),
  ]);

  public passwordConfirmation = new UntypedFormControl(null, [
    Validators.required.bind(this),
  ]);

  public showErrors = false;

  public passwordSubmitted = false;

  public showForm = true;

  private uuid: string = '';

  private token: string = '';

  public resetError = false;

  public isUnihomesDeeplink: boolean = false;

  public deeplink: string | undefined;

  private uniHomesDeeplink: string | null | undefined;

  private unihomeCMSID!: string | undefined;

  private isApp: boolean = false;

  public get passwordsNotEqual(): boolean {
    return this.password.value !== this.passwordConfirmation.value;
  }

  constructor(
    public override readonly titleService: Title,
    public override readonly metaService: Meta,
    public override readonly strapi: StrapiService,
    public readonly formBuilder: UntypedFormBuilder,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute
  ) {
    super(titleService, metaService, strapi);
  }

  ngOnInit(): void {
    // make sure the user has a csrf token
    this.authService.sanctumCSRF().subscribe({
      next: () => {
        this.authService.csrfLoaded = true;
      },
    });

    this.strapi
      .getResetPassword()
      .subscribe((data: IStrapiDocument<ResetPassword>) => {
        this.parsePageData(data.data.attributes);
        super.setMetas();
        this.cdr.detectChanges();
      });

    const routeParams = this.route.snapshot.paramMap;
    const { queryParams } = this.route.snapshot;
    this.uuid = String(routeParams.get('uuid'));
    this.token = String(routeParams.get('token'));
    this.isApp = JSON.parse(queryParams['is-app'] as string);
    this.unihomeCMSID = queryParams['unihomes'];

    this.isUnihomesDeeplink = !!this.unihomeCMSID;

    if (this.isUnihomesDeeplink) {
      this.uniHomesDeeplink = this.unihomeCMSID;

      const deeplinkToApp = `https://endsleigh.page.link/?link=https://my.endsleigh.co.uk/registration?universityUUID%3D${this
        .uniHomesDeeplink!}&apn=com.endsleighwalletapp.EndsleighWalletApp&isi=1566334255&ibi=com.endsleigh.app`;
      let baseLink = '';

      if (environment.name !== 'production' || !this.unihomeCMSID) {
        baseLink = !this.isApp
          ? `${environment.appHost}/registration?universityUUID=${this
              .uniHomesDeeplink!}`
          : deeplinkToApp;
      } else {
        baseLink = deeplinkToApp;
      }

      this.deeplink = baseLink;
    }
  }

  protected override parsePageData(page: ResetPassword): void {
    super.parsePageData(page);
    this.pageData = page;
  }

  public onFocus(): void {
    this.showErrors = false;
  }

  public onBlur(): void {
    this.showErrors = true;
  }

  public onSubmit(): void {
    this.passwordSubmitted = true;

    if (this.passwordsNotEqual || this.password.invalid) {
      return;
    }

    this.showForm = false;

    this.authService
      .resetPassword(this.password.value as string, this.uuid, this.token)
      .subscribe({
        next: (): void => {
          this.showForm = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.resetError = true;
          this.cdr.markForCheck()
        },
      });
  }

  /**
   * return reset password success message
   * @returns String success message
   */
  public get successMessage(): string {
    if (this.isUnihomesDeeplink) {
      return this.pageData.unihome_success_message ?? '';
    }
    return this.pageData.success_message ?? '';
  }

  /**
   * return the reset password success route
   * @returns String route
   */
  public get resetPasswordRoute(): string {
    if (this.isUnihomesDeeplink) {
      return this.deeplink!;
    }
    return '/';
  }

  /**
   * return the success button text
   * @returns String
   */
  public get successButtonText(): string {
    if (this.isUnihomesDeeplink) {
      return this.pageData.unihome_button_text ?? '';
    }
    return this.pageData.login_button_text ?? '';
  }
}
